import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { FlyInTransition } from '../../components/transitions/transitions';
import styled from 'styled-components';
import StarOutlinedWhiteIcon from '../../images/Claims-Page/StarOutlinedWhite.svg';
import claimsFeedbackIcon from '../../images/claims-feedback.svg';
import RatingStarIcon from '@web/images/common/RatingStar.svg';
import MessageIcon from '@web/images/common/Message.svg';
import StarsOverlay from '@web/images/common/StarsOverlay.svg';

const queryImage =
  'https://visit-public-stage.s3.ap-south-1.amazonaws.com/query_raise.png';
const uploadImage =
  'https://visit-public-stage.s3.ap-south-1.amazonaws.com/upload_icon.png';

const PrescriptionText = styled.h1`
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  @media (max-width: 400px) {
    font-size: 12px;
  }
  @media (max-width: 360px) {
    font-size: 11px;
  }
`;
const DoctorText = styled.p`
  color: #fff;
  text-align: center;
  font-size: 12px;
  @media (max-width: 400px) {
    font-size: 10px;
  }
`;

const SnackBarWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: 440px;
  z-index: 40;
  display: flex;
  flex-direction: column;
`;

const ScrollContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const PaginationBar = styled.div`
  width: 100%;
  background: #fff;
  display: flex;
  gap: 2px;
  padding: 2px 2px;
`;

const BarSegment = styled.div`
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: ${(props) => (props.active ? 'var(--primary-button, #714fff)' : '#CFC8FF')};
  transition: background 0.2s;
`;

const STARS = [1, 2, 3, 4, 5];

function SnackBar({
  data,
  showModal,
  closeModal,
  feedbackData,
  onFeedbackTap,
  onFeedbackDismiss,
  supportCards,
  onSupportTap,
}) {
  const history = useHistory();
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.offsetWidth);
    setActiveIndex(idx);
  }, []);

  const handleNavigation = () => {
    history.push('/consultation/appointment/appointment-status', {
      requestId: data.pendingRequestId,
      goBack: true,
      blockers: data,
    });
  };
  const hasPrescription = !!data;
  const isRejected = data && data.prescriptionVerificationStatus === 'rejected';
  const gradientColor = isRejected
    ? 'linear-gradient(to right, #FD2E2E, #FF8989)'
    : 'linear-gradient(to right, var(--primary-button, #714fff), #A28BFF)';
  const prescriptionIcon = isRejected ? queryImage : uploadImage;
  const clinicText = isRejected ? 'In-Clinic' : 'Offline';
  const uploadButtonText = isRejected ? '#E02323' : 'var(--primary-button, #714fff)';
  const prescriptionText = isRejected
    ? 'Prescription Query'
    : 'Upload Prescription';

  const showFeedback = !!(feedbackData && onFeedbackTap);
  const supportSlides = Array.isArray(supportCards) ? supportCards : [];
  const slideCount =
    (hasPrescription ? 1 : 0) + (showFeedback ? 1 : 0) + supportSlides.length;
  const multiSlide = slideCount > 1;
  const supportStartIndex = (hasPrescription ? 1 : 0) + (showFeedback ? 1 : 0);
  const activeSupportCard =
    activeIndex >= supportStartIndex
      ? supportSlides[activeIndex - supportStartIndex]
      : null;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = 0;
    setActiveIndex(0);
  }, [slideCount]);

  useEffect(() => {
    if (!multiSlide) return;
    const interval = setInterval(() => {
      const el = scrollRef.current;
      if (!el || !el.offsetWidth) return;
      const cur = Math.round(el.scrollLeft / el.offsetWidth);
      const next = (cur + 1) % slideCount;
      el.scrollTo({
        left: next * el.offsetWidth,
        behavior: next === 0 ? 'auto' : 'smooth',
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [multiSlide, slideCount]);

  return (
    <div>
      <FlyInTransition
        in={showModal}
        mountOnEnter
        unmountOnExit
        appear={multiSlide}
      >
        <SnackBarWrapper>
          {supportSlides.length > 0 && activeSupportCard && (
            <div
              className="absolute bg-white flex items-center justify-center z-20 pointer-events-none left-4 -top-1"
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
              }}
              aria-hidden="true"
            >
              <img
                src={
                  activeSupportCard.type === 'support-rating'
                    ? RatingStarIcon
                    : MessageIcon
                }
                alt=""
                className="object-contain scale-150 w-9 h-9"
              />
            </div>
          )}
          <ScrollContainer ref={scrollRef} onScroll={handleScroll}>
            {hasPrescription && (
              <div
                className="flex items-center text-textbox justify-between rounded-t-12 h-60px"
                style={{
                  flexShrink: 0,
                  width: '100%',
                  scrollSnapAlign: 'start',
                  backgroundImage: gradientColor,
                  boxShadow: '0px -5px 5px rgba(0,0,0,0.1) inset',
                  padding: '10px',
                }}
              >
                <div className="flex w-full items-center">
                  <div className="bg-textbox rounded-full w-60px flex justify-center mt-[-10px] h-[52px] max-h-[60px]">
                    <img
                      src={prescriptionIcon}
                      style={{ height: '52px', width: '52px' }}
                      alt="prescription"
                    />
                  </div>
                  <div className="flex justify-between w-full px-1 items-center">
                    <div className="flex flex-col justify-start items-center border-r pr-16px">
                      <PrescriptionText>{prescriptionText}</PrescriptionText>
                      <DoctorText>
                        {clinicText} | {data.doctorName}
                      </DoctorText>
                    </div>
                    <div className="pt-8 px-8 flex justify-center items-center">
                      <button
                        className="text-textbox rounded-8 w-100px h-24px text-body-l font-semibold"
                        style={{
                          background:
                            'linear-gradient(180deg, #FFFFFF, #E4E4E4)',
                          fontWeight: '600',
                          color: uploadButtonText,
                        }}
                        onClick={handleNavigation}
                      >
                        Upload
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showFeedback && (
              <div
                className="flex items-center text-textbox justify-between rounded-t-12 h-60px"
                style={{
                  flexShrink: 0,
                  width: '100%',
                  scrollSnapAlign: 'start',
                  backgroundImage:
                    'linear-gradient(to right, var(--primary-button, #714fff), #A28BFF)',
                  padding: '0 10px',
                }}
                onClick={onFeedbackTap}
              >
                <div className="flex w-full items-center" style={{ gap: 12 }}>
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={claimsFeedbackIcon}
                      alt=""
                      style={{ width: 44, height: 44, objectFit: 'contain' }}
                    />
                  </div>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: '#fff',
                      flex: 1,
                    }}
                  >
                    Rate your claims experience
                  </span>
                  <div
                    className="flex items-center"
                    style={{ gap: 3, flexShrink: 0, marginRight: '20px' }}
                  >
                    {STARS.map((i) => (
                      <img
                        key={i}
                        src={StarOutlinedWhiteIcon}
                        alt=""
                        style={{ width: 18, height: 18 }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            {supportSlides.map((card) => {
              const { type, attributes } = card;
              const { title, buttonText, buttonColor, ticketId } =
                attributes || {};
              const gradient =
                type === 'support-rating'
                  ? 'linear-gradient(to right, #FD2E2E, #FF8989)'
                  : 'linear-gradient(to right, #003D99, #6A8CD6)';
              return (
                <div
                  key={`${type}-${ticketId}`}
                  className="flex items-center text-textbox justify-between rounded-t-12 w-full py-0 px-4 shadow-2xl relative"
                  style={{
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                    backgroundImage: gradient,
                  }}
                >
                  <div
                    className="absolute inset-0 pointer-events-none"
                    aria-hidden="true"
                  >
                    <img
                      src={StarsOverlay}
                      alt=""
                      className="w-full h-full object-cover block"
                    />
                  </div>
                  <div className="flex w-full items-center relative z-10 gap-3">
                    <div
                      style={{ width: 52, height: 52, flexShrink: 0 }}
                      aria-hidden="true"
                    />
                    <span className="font-bold text-sm text-white flex-1">
                      {title}
                    </span>
                    <div
                      style={{
                        width: 1,
                        height: 28,
                        background: '#FBFBFB',
                        flexShrink: 0,
                      }}
                    />
                    <button
                      type="button"
                      className="text-xs font-semibold rounded-8 px-3 py-1"
                      style={{
                        background: 'linear-gradient(180deg, #FFFFFF, #E4E4E4)',
                        color: buttonColor,
                        marginRight: 8,
                        flexShrink: 0,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onSupportTap) onSupportTap(card);
                      }}
                    >
                      {buttonText}
                    </button>
                  </div>
                </div>
              );
            })}
          </ScrollContainer>
          {multiSlide && (
            <PaginationBar>
              {Array.from({ length: slideCount }, (_, i) => (
                <BarSegment key={i} active={activeIndex === i} />
              ))}
            </PaginationBar>
          )}
        </SnackBarWrapper>
      </FlyInTransition>
    </div>
  );
}

export default SnackBar;
